/**
 * Product events hooks (super admin): first-party tracking, generic across all events.
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api';

export interface FunnelCohortStep {
  event: string;
  users: number;
  conversionFromPrevious: number | null;
  conversionFromFirst: number | null;
}

export interface FunnelStepCount {
  event: string;
  total: number;
  uniqueUsers: number;
}

export interface FunnelData {
  days: number;
  since: string;
  cohort: FunnelCohortStep[];
  stepCounts: FunnelStepCount[];
  failures: { total: number; byReason: { reason: string; total: number }[] };
  nonFailureVoids: number;
}

export type EventCategory = 'shop' | 'upsell' | 'gating' | 'checkout' | 'provisioning' | 'other';

export interface RegistryEntry {
  event: string;
  label: string;
  category: EventCategory;
}

export interface SummaryEvent extends RegistryEntry {
  total: number;
  uniqueUsers: number;
}

export interface SummaryData {
  days: number;
  since: string;
  registry: RegistryEntry[];
  events: SummaryEvent[];
  daily: { date: string; total: number }[];
}

export interface RecentProductEvent {
  id: string;
  event: string;
  source: 'CLIENT' | 'SERVER';
  createdAt: string;
  props: Record<string, unknown>;
  user: { id: number; email: string; name: string | null } | null;
  team: { id: number; name: string } | null;
  agencyId: number | null;
}

export interface RecentEventsData {
  days: number;
  since: string;
  events: RecentProductEvent[];
}

export interface UserTimelineEvent {
  id: number;
  event: string;
  userId: number | null;
  teamId: number | null;
  agencyId: number | null;
  source: 'CLIENT' | 'SERVER';
  props: Record<string, unknown>;
  createdAt: string;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const productEventKeys = {
  funnel: (days: number) => ['admin', 'product-events', 'funnel', days] as const,
  summary: (days: number) => ['admin', 'product-events', 'summary', days] as const,
  recent: (days: number, event: string, category: string, userId: number | null) =>
    ['admin', 'product-events', 'recent', days, event, category, userId] as const,
  timeline: (userId: number) => ['admin', 'product-events', 'timeline', userId] as const,
};

export function useShopFunnel(days: number, enabled = true) {
  return useQuery({
    enabled,
    queryKey: productEventKeys.funnel(days),
    queryFn: async (): Promise<FunnelData> => {
      const res = await apiClient.get<Envelope<FunnelData>>('/api/admin/product-events/funnel', {
        params: { days },
      });
      return res.data.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}

export function useProductEventSummary(days: number) {
  return useQuery({
    queryKey: productEventKeys.summary(days),
    queryFn: async (): Promise<SummaryData> => {
      const res = await apiClient.get<Envelope<SummaryData>>('/api/admin/product-events/summary', {
        params: { days },
      });
      return res.data.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}

export function useRecentProductEvents(
  days: number,
  event: string,
  category: string,
  userId: number | null,
  enabled = true
) {
  return useQuery({
    enabled,
    queryKey: productEventKeys.recent(days, event, category, userId),
    queryFn: async (): Promise<RecentEventsData> => {
      const params: Record<string, string | number> = { days, limit: 100 };
      if (event) params.event = event;
      if (category) params.category = category;
      if (userId) params.userId = userId;
      const res = await apiClient.get<Envelope<RecentEventsData>>(
        '/api/admin/product-events/recent',
        { params }
      );
      return res.data.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}

export function useUserEventTimeline(userId: number | null) {
  return useQuery({
    queryKey: productEventKeys.timeline(userId ?? 0),
    queryFn: async (): Promise<UserTimelineEvent[]> => {
      const res = await apiClient.get<Envelope<UserTimelineEvent[]>>(
        `/api/admin/users/${userId}/events`,
        { params: { days: 90, limit: 200 } }
      );
      return res.data.data;
    },
    enabled: userId !== null && userId > 0,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}
